import {
    ALERT_TYPE_LABEL,
    RoutingBackstop,
    RoutingOverview,
    RoutingPerson,
    RoutingRule,
} from '../../types/notification-rules.types';

/**
 * The single place that turns a routing payload into sentences.
 *
 * Every surface that mentions who gets notified — the Notifications page, the
 * Team Leaders card, the system user form — renders these, so the wording
 * cannot say one thing on one screen and something else on another. The facts
 * themselves (counts, names, stages) always come from the API.
 */

export const joinList = (items: string[]): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** "all 13 admins, as they do today" / "Maria Delgado, the point of contact" */
export const backstopLabel = (backstop: RoutingBackstop): string => {
    if (backstop.kind === 'point-of-contact' && backstop.person) {
        return `${backstop.person.name}, the organization point of contact`;
    }
    if (backstop.kind === 'admin-fallback') {
        return `all ${backstop.adminCount} ${plural(backstop.adminCount, 'admin', 'admins')}, as they do today`;
    }
    return 'no one';
};

/** Alert types whose recipients come from team structure. */
export const leaderRoutedTypes = (rules: RoutingRule[]): RoutingRule[] =>
    rules.filter((r) => r.teamRouting);

/**
 * The sentence shown on a team's Leaders card. Generated rather than written
 * into the component so it follows the rules, including when an alert type is
 * handed to a security desk and leaders are out of it entirely.
 */
export const teamLeaderSentence = (
    overview: RoutingOverview,
    team: { leaderCount: number; unreachableLeaderCount: number },
): string => {
    const routed = leaderRoutedTypes(overview.rules);
    const named = overview.rules.filter((r) => !r.teamRouting);

    if (routed.length === 0) {
        return 'Leaders of this team do not receive alerts.'
            + ' Recipients are set in Settings, under Notifications.';
    }

    const list = routed.map((r) => `${ALERT_TYPE_LABEL[r.alertType]} (${joinList(r.stages) || 'no stage'})`);
    let text = `Leaders of this team receive ${joinList(list)} alerts for its POM Users.`;

    if (named.length) {
        text += ` ${joinList(named.map((r) => ALERT_TYPE_LABEL[r.alertType]))} ${plural(named.length, 'goes', 'go')} to named people instead.`;
    }
    if (team.leaderCount === 0) {
        text += ` This team has no leaders, so its alerts fall to ${backstopLabel(overview.backstop)}.`;
    } else if (team.unreachableLeaderCount === team.leaderCount) {
        text += ` None of them has email, text or call set up, so alerts go to ${backstopLabel(overview.backstop)}.`;
    } else if (team.unreachableLeaderCount > 0) {
        text += ` ${team.unreachableLeaderCount} of them will be skipped until they set up email, text or call.`;
    }

    return text;
};

/**
 * The one line under an alert type that says what will actually happen, in
 * words someone who knows only the alerts and their people can follow.
 *
 * It says nothing when there is nothing worth knowing. Amber means alerts
 * still reach someone, just not the people chosen. Red is reserved for the
 * one case that matters: nobody at all would be told.
 */
export interface AlertStatus {
    tone: 'warn' | 'bad';
    text: string;
}

/** "your 12 admins" or "Maria Delgado"; null when nobody would be told. */
const fallbackTarget = (o: RoutingOverview): string | null => {
    if (o.backstop.kind === 'point-of-contact' && o.backstop.person) return o.backstop.person.name;
    if (o.backstop.kind === 'admin-fallback') {
        return o.backstop.adminCount === 1 ? 'your admin' : `your ${o.backstop.adminCount} admins`;
    }
    return null;
};

const peopleLabel = (people: RoutingPerson[]) => (people.length <= 2
    ? joinList(people.map((p) => p.name))
    : `${people.length} people`);

const NO_ONE = 'Choose a point of contact below.';

/**
 * `subject` lets the page say the same thing once for every alert type that
 * shares it ("team leader alerts") instead of repeating it on each row.
 */
export const alertStatus = (
    rule: RoutingRule,
    o: RoutingOverview,
    subject = 'these alerts',
): AlertStatus | null => {
    const to = fallbackTarget(o);
    const fallsBack = (why: string): AlertStatus => (to
        ? { tone: 'warn', text: `${why}, so ${subject} go to ${to}.` }
        : { tone: 'bad', text: `${why}, so no one would be notified. ${NO_ONE}` });
    const skipped = (people: RoutingPerson[]): AlertStatus => ({
        tone: 'warn',
        text: `${peopleLabel(people)} ${plural(people.length, 'has', 'have')} no email, text or call set up, so `
            + `they will be skipped.`,
    });

    if (rule.teamRouting) {
        const { teamCount, teamsWithoutLeader, leaders, subscribers } = o.teamStructure;
        if (teamCount === 0) return fallsBack("You don't have any teams yet");

        if (leaders.length === 0) {
            if (subscribers.length === 0) return fallsBack('No team has a leader yet');
            const except = `except for teams ${peopleLabel(subscribers)} ${plural(subscribers.length, 'follows', 'follow')}`;
            return to
                ? { tone: 'warn', text: `No team has a leader yet, so ${subject} go to ${to}, ${except}.` }
                : { tone: 'bad', text: `No team has a leader yet, so ${subject} reach no one, ${except}. ${NO_ONE}` };
        }

        if (!leaders.some((l) => l.reachable)) return fallsBack('None of your team leaders has email, text or call set up');

        if (teamsWithoutLeader > 0) {
            const n = teamsWithoutLeader;
            const lead = `${n} ${plural(n, 'team has', 'teams have')} no leader.`;
            const from = `Alerts from ${plural(n, 'that team', 'those teams')}`;
            return to
                ? { tone: 'warn', text: `${lead} ${from} go to ${to}.` }
                : { tone: 'bad', text: `${lead} ${from} would reach no one. ${NO_ONE}` };
        }

        const unreachable = leaders.filter((l) => !l.reachable);
        return unreachable.length ? skipped(unreachable) : null;
    }

    // An empty list never reaches here: the API resolves it as team leaders.
    if (rule.named.length === 0) return null;
    if (!rule.named.some((p) => p.reachable)) return fallsBack('None of these people has email, text or call set up');
    const unreachable = rule.named.filter((p) => !p.reachable);
    return unreachable.length ? skipped(unreachable) : null;
};
